import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  addDoc,
  doc,
  getDocs,
  updateDoc,
  deleteDoc,
  Timestamp
} from 'firebase/firestore';
import Modal from 'react-modal';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';

Modal.setAppElement('#root');

const Ranks = () => {
  const { userData } = useUserAuth();
  const [update, setUpdate] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [ranks, setRanks] = useState([]);
  const [salaryComponents, setSalaryComponents] = useState([]);
  const [filteredRanks, setFilteredRanks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    gradeLevel: '',
    basicSalary: '',
    components: []
  });

  const [editingRank, setEditingRank] = useState(null);
  const [deletingRank, setDeletingRank] = useState(null);
  const [viewingRank, setViewingRank] = useState(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isFetching, setIsFetching] = useState(true);

  const ranksCollectionRef = collection(db, "ranks");
  const salaryStructureCollectionRef = collection(db, "salaryStructure");

  useEffect(() => {
    fetchSalaryComponents();
    fetchRanks();
  }, []);

  const fetchSalaryComponents = async () => {
    try {
      const snap = await getDocs(salaryStructureCollectionRef);
      const list = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSalaryComponents(list);
    } catch (err) {
      setError("Failed to load salary components");
      setShowModal(true);
    }
  };

  const fetchRanks = async () => {
    setIsFetching(true);
    try {
      const snap = await getDocs(ranksCollectionRef);
      const list = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRanks(list);
    } catch (err) {
      setError("Failed to load ranks");
      setShowModal(true);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    const filtered = ranks.filter(rank =>
      rank.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (rank.gradeLevel && rank.gradeLevel.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredRanks(filtered);
  }, [searchTerm, ranks]);

  const handleComponentValueChange = (componentId, value) => {
    setFormData(prev => ({
      ...prev,
      components: prev.components.map(c =>
        c.id === componentId ? { ...c, value: parseFloat(value) || 0 } : c
      )
    }));
  };

  const toggleComponent = (component) => {
    setFormData(prev => {
      const exists = prev.components.find(c => c.id === component.id);
      if (exists) {
        return { ...prev, components: prev.components.filter(c => c.id !== component.id) };
      } else {
        return {
          ...prev,
          components: [...prev.components, {
            id: component.id,
            name: component.name,
            type: component.type,
            calculation: component.calculation,
            percentageOf: component.percentageOf,
            value: 0
          }]
        };
      }
    });
  };

  const handleAddRank = async () => {
    if (!formData.name.trim() || !formData.gradeLevel.trim() || !formData.basicSalary || formData.basicSalary <= 0) {
      setError("Rank name, grade level, and basic salary are required");
      setShowModal(true);
      return;
    }

    setUpdate(true);
    try {
      await addDoc(ranksCollectionRef, {
        name: formData.name.trim(),
        gradeLevel: formData.gradeLevel.trim(),
        basicSalary: parseFloat(formData.basicSalary),
        components: formData.components,
        createdAt: Timestamp.now()
      });
      setSuccessMessage("Rank added successfully");
      fetchRanks();
      resetForm();
      setIsAddModalOpen(false);
    } catch (err) {
      setError("Failed to add rank");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleUpdateRank = async () => {
    setUpdate(true);
    try {
      const rankDoc = doc(db, "ranks", editingRank.id);
      await updateDoc(rankDoc, {
        name: formData.name.trim(),
        gradeLevel: formData.gradeLevel.trim(),
        basicSalary: parseFloat(formData.basicSalary),
        components: formData.components
      });
      setSuccessMessage("Rank updated successfully");
      fetchRanks();
      setIsEditModalOpen(false);
      setEditingRank(null);
      resetForm();
    } catch (err) {
      setError("Failed to update rank");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const openEdit = (rank) => {
    setEditingRank(rank);
    setFormData({
      name: rank.name || '',
      gradeLevel: rank.gradeLevel || '',
      basicSalary: rank.basicSalary || '',
      components: rank.components || []
    });
    setIsEditModalOpen(true);
  };

  const openView = (rank) => {
    setViewingRank(rank);
    setIsViewModalOpen(true);
  };

  const confirmDelete = (rank) => {
    setDeletingRank(rank);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    setUpdate(true);
    try {
      await deleteDoc(doc(db, "ranks", deletingRank.id));
      setSuccessMessage("Rank deleted successfully");
      fetchRanks();
      setIsDeleteModalOpen(false);
    } catch (err) {
      setError("Failed to delete rank");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      gradeLevel: '',
      basicSalary: '',
      components: []
    });
  };

  // Calculate Salary Summary (for list & view)
  const calculateSalary = (rank) => {
    const basic = parseFloat(rank.basicSalary) || 0;
    let gross = basic;
    let basicAdditions = 0;
    let grossAdditions = 0;
    let totalDeductions = 0;

    rank.components?.forEach(comp => {
      const value = parseFloat(comp.value) || 0;
      if (comp.type === 'addition') {
        if (comp.calculation === 'percentage') {
          if (comp.percentageOf === 'basic') {
            basicAdditions += (basic * value) / 100;
          } else {
            grossAdditions += (gross * value) / 100;
          }
        } else {
          gross += value;
        }
      }
    });

    gross += basicAdditions;
    gross += grossAdditions;

    rank.components?.forEach(comp => {
      if (comp.type === 'subtraction') {
        const value = parseFloat(comp.value) || 0;
        if (comp.calculation === 'percentage') {
          totalDeductions += comp.percentageOf === 'basic'
            ? (basic * value) / 100
            : (gross * value) / 100;
        } else {
          totalDeductions += value;
        }
      }
    });

    const net = gross - totalDeductions;

    return {
      gross: gross.toFixed(2),
      deductions: totalDeductions.toFixed(2),
      net: net.toFixed(2)
    };
  };

  if (!userData) return <Loader text="READING" />;

  const allowedRoles = ['Admin', 'Supreme'];

  return (
    <>
      {update && <Loader text="PROCESSING" />}

      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <h2 className="manage-page-heading caps size4 margin-top-cashflow1">Manage Ranks & Pay</h2>
            <Link to="#" className="home-link" onClick={(e) => { e.preventDefault(); fetchRanks(); }}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>

          <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed scroll-fixed6">
            <button className="login-button login-button-d caps" onClick={() => { resetForm(); setIsAddModalOpen(true); }}>
              Add Rank
            </button>

            <input
              type="text"
              className="input-field1"
              placeholder="Search by rank name or grade level..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <div className="scroll-fixed scroll-fixed1">
              <div className="class-list class-list-d scrollable">
                {isFetching ? (
                  <p className="loading-text">Loading ranks...</p>
                ) : filteredRanks.length === 0 ? (
                  <p className="loading-text">No ranks found.</p>
                ) : (
                  filteredRanks.map((rank) => {
                    const { gross, deductions, net } = calculateSalary(rank);
                    return (
                      <div key={rank.id} className="class-item">
                        <div className="column gap-1">
                          <p>
                            <strong>{rank.name}</strong><br  />
                            {rank.gradeLevel && <><span>{rank.gradeLevel}</span><br /></>}
                            <span>Basic: ₦{parseFloat(rank.basicSalary).toLocaleString()}</span><br />
                            <span>Gross: ₦{parseFloat(gross).toLocaleString()}</span><br />
                            <span>Deductions: ₦{parseFloat(deductions).toLocaleString()}</span><br />
                            <strong>Net Pay: ₦{parseFloat(net).toLocaleString()}</strong>
                          </p>                          
                        </div>
                        <div className="row align-center justify-center gap-15">
                          <button className="list-btn" onClick={() => openView(rank)}>
                            <i className="fas fa-eye"></i>
                          </button>
                          <button className="list-btn" onClick={() => openEdit(rank)}>
                            <i className="fas fa-edit"></i>
                          </button>
                          <button className="list-btn delete-btn" onClick={() => confirmDelete(rank)}>
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>                        
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}

      {/* Success/Error Modal */}
      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => { setError(""); setSuccessMessage(""); setShowModal(false); }}
        />
      )}

      {/* View Salary Structure Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onRequestClose={() => setIsViewModalOpen(false)}
        className="custom-modal modal-scroll"
        overlayClassName="custom-modal-overlay"
      >
        <p className="size5 bold caps">Full Salary Structure</p>
        {viewingRank && (
          <div className="column gap-15 modal-scroll">
            <p>
              <span>{viewingRank.name}</span><br />
              <span>{viewingRank.gradeLevel && `${viewingRank.gradeLevel}`}</span>
            </p>

            <div className="row justify-between">
              <strong>Basic Salary</strong>&nbsp;
              <span>₦{parseFloat(viewingRank.basicSalary).toLocaleString()}</span>
            </div>

            {viewingRank.components && viewingRank.components.length > 0 ? (
              <>
                {viewingRank.components
                  .filter(c => c.type === 'addition')
                  .map((comp, i) => (
                    <div key={i} className="row justify-between font-green">
                      <span className="bold">+ {comp.name}</span>&nbsp;
                      <span>
                        {comp.calculation === 'percentage'
                          ? `${comp.value}% of ${comp.percentageOf === 'gross' ? 'Gross Pay' : 'Basic Salary'}`
                          : `₦${parseFloat(comp.value).toLocaleString()}`
                        }
                      </span>
                    </div>
                  ))}

                {viewingRank.components
                  .filter(c => c.type === 'subtraction')
                  .map((comp, i) => (
                    <div key={i} className="row justify-between font-red">
                      <span className="bold">− {comp.name}</span>&nbsp;
                      <span>
                        {comp.calculation === 'percentage'
                          ? `${comp.value}% of ${comp.percentageOf === 'gross' ? 'Gross Pay' : 'Basic Salary'}`
                          : `₦${parseFloat(comp.value).toLocaleString()}`
                        }
                      </span>
                    </div>
                  ))}

                {(() => {
                  const { gross, deductions, net } = calculateSalary(viewingRank);
                  return (
                    <>
                      <div className="row justify-between">
                        <strong>Gross Pay:</strong>&nbsp;
                        <strong>₦{parseFloat(gross).toLocaleString()}</strong>
                      </div>
                      <div className="row justify-between">
                        <strong>Total Deductions:</strong>&nbsp;
                        <strong>₦{parseFloat(deductions).toLocaleString()}</strong>
                      </div>
                      <div className="row justify-between bold caps">
                        <strong>Net Pay:</strong>&nbsp;
                        <strong>₦{parseFloat(net).toLocaleString()}</strong>
                      </div>
                    </>
                  );
                })()}
              </>
            ) : (
              <p className="font-gray">No components attached.</p>
            )}

            <button className="login-button caps mt-30" onClick={() => setIsViewModalOpen(false)}>
              Close
            </button>
          </div>
        )}
      </Modal>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isAddModalOpen || isEditModalOpen}
        onRequestClose={() => {
          setIsAddModalOpen(false);
          setIsEditModalOpen(false);
          setEditingRank(null);
          resetForm();
        }}
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>{isEditModalOpen ? 'Edit' : 'Add'} Rank</h2>
        <div className="product-form modal-scroll">
          <label className="input-label">Name</label>
          <input type="text" className="input-field" placeholder="Rank Name" value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} />
          <label className="input-label">Grade</label>
          <input type="text" className="input-field mt-10" placeholder="Grade Level e.g. GL-10" value={formData.gradeLevel} onChange={(e) => setFormData(prev => ({ ...prev, gradeLevel: e.target.value }))} />
          <label className="input-label">Basic Salary (₦)</label>
          <input type="number" className="input-field mt-10" placeholder="Basic Salary (₦)" value={formData.basicSalary} onChange={(e) => setFormData(prev => ({ ...prev, basicSalary: e.target.value }))} />

          <div>
            <h4 className="caps size5">Select Components</h4>
            {salaryComponents.length === 0 ? (
              <p className="font-gray">Create components in Salary Structure first.</p>
            ) : (
              salaryComponents.map(comp => {
                const selected = formData.components.find(c => c.id === comp.id);
                const value = selected?.value || 0;
                return (
                  <div key={comp.id} className="mb-15 p-10 border-gold-round">
                    <label className="row align-center gap-10">
                      <input type="checkbox" checked={!!selected} onChange={() => toggleComponent(comp)} className="gold-tick" />
                      <strong>{comp.name}</strong>&nbsp;
                      <span className={`${comp.type === 'addition' ? 'font-teal' : 'font-red'}`}>
                        ({comp.type === 'addition' ? '+' : '−'} {comp.calculation === 'percentage' ? `${comp.percentageOf === 'gross' ? 'Gross' : 'Basic'} %` : 'Fixed'})
                      </span>
                    </label>
                    {selected && (
                      <>
                        <label className="input-label">{comp.calculation === 'percentage' ? 'Percentage (%)' : 'Amount (₦)'}</label>
                        <input
                          type="number"
                          className="input-field"
                          placeholder={comp.calculation === 'percentage' ? "Percentage (e.g. 15)" : "Amount (₦)"}
                          value={value}
                          onChange={(e) => handleComponentValueChange(comp.id, e.target.value)}
                        />
                      </>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <div className="row justify-center gap-15 mt-30">
            <button className="login-button" onClick={() => { setIsAddModalOpen(false); setIsEditModalOpen(false); resetForm(); }}>Cancel</button>
            <button className="login-button" onClick={isEditModalOpen ? handleUpdateRank : handleAddRank}>
              {isEditModalOpen ? 'Update' : 'Add'} Rank
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Modal */}
      <Modal isOpen={isDeleteModalOpen} onRequestClose={() => setIsDeleteModalOpen(false)} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Delete Rank</h2>
        <p>Delete <strong>{deletingRank?.name}</strong>{deletingRank?.gradeLevel && ` (${deletingRank.gradeLevel})`}?</p>
        <div className="row justify-center gap-15">
          <button className="login-button" onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
          <button className="login-button delete-btn" onClick={handleDelete}>Delete</button>
        </div>
      </Modal>
    </>
  );
};

export default Ranks;